import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { Button, Select, Spin, notification } from 'antd';
import { useAppSelector } from 'app/store';
import { useNavigate, useParams } from 'react-router-dom';
import Breadcrumbs from 'shared/components/breadcrumbs/Breadcrumbs';
import TextEditor from 'shared/components/text-editor/TextEditor';
import Title from 'shared/components/title/Title';
import { FaqStatuses } from 'shared/enums/FAQ';
import {
  useDeleteFaqMutation,
  useGetFaqByIdQuery,
  useGetFaqCategoryQuery,
  useUpdateFaqMutation,
} from 'app/services/edpApiV1/faq';
import { SimpleIcon, SimpleIconName } from 'shared/components/icons/icons';
import { IUpdateFAQ, TCategorySelect, TFaqCategoryPayload } from 'shared/types/faq.types';
import { DEFAILT_CATEGORY, DEFAULT_CATEGORY_VALUE } from 'shared/constants/faq';
import { addNotifications } from 'shared/helpers/utils';
import ModalCancelSubmit from '../../../shared/components/modalCancelSubmit/ModalCancelSubmit';
import style from './index.module.scss';

const UpdateFaq = () => {
  const { id } = useParams();
  const [openCancelModal, setOpenCanelModal] = useState<boolean>(false);
  const me = useAppSelector(state => state.entities.viewer.me);
  const navigate = useNavigate();
  const [updateFaqQuery, { isLoading: isUpdateLoading }] = useUpdateFaqMutation();
  const { data: categories } = useGetFaqCategoryQuery({});
  const { data } = useGetFaqByIdQuery({ id: Number(id) });
  const [deleteFaqQuery, { isLoading: isDeleteLoading }] = useDeleteFaqMutation();

  const [title, setTitle] = useState<string>(data?.title ?? '');
  const [content, setContent] = useState<string>(data?.content ?? '');
  const [categoryId, setCategoryId] = useState<TCategorySelect>(data?.category ? data?.category.id : 'null');

  useEffect(() => {
    if (data) {
      setTitle(data.title);
      setContent(data.content);
      setCategoryId(data?.category ? data?.category.id : 'null');
    }
  }, [data]);

  const breadCrumbs = [
    { name: 'Часто задаваемые вопросы', link: '/faq' },
    { name: 'Редактировать вопрос', link: `/faq/update` },
  ];

  const getCategoryRow = (data: TFaqCategoryPayload, isDefault = false) => {
    return {
      value: isDefault ? 'null' : data.id,
      label: (
        <div className={style['category-option']}>
          <SimpleIcon name={data.icon as SimpleIconName} /> <span>{data.name}</span>
        </div>
      ),
    };
  };

  const options = useMemo(() => {
    if (!categories?.data) {
      return;
    }
    return [...categories?.data.map(elem => getCategoryRow(elem)), getCategoryRow(DEFAILT_CATEGORY, true)];
  }, [categories?.data]);

  const prepareFaqToSave = (status?: FaqStatuses): IUpdateFAQ => {
    if (data) {
      return {
        id: data?.id,
        created: data.created,
        title: title,
        content: content,
        status: status || FaqStatuses.UNPUBLICATED,
        categoryId: categoryId === 'null' ? DEFAULT_CATEGORY_VALUE : categoryId,
      };
    }
    throw new Error('Вопроса-ответа не существует');
  };

  const onTitleChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setTitle(event.target.value);
  };

  const onSubmitCancelModal = () => {
    setOpenCanelModal(false);
    navigate('/faq');
  };

  const onCancelCancelModal = () => {
    setOpenCanelModal(false);
  };

  const cancelModalOpen = () => {
    setOpenCanelModal(true);
  };

  const navigateAfterSuccess = () => {
    navigate('/faq');
  };

  const createFaq = async () => {
    await addNotifications(
      updateFaqQuery(prepareFaqToSave()),
      'Вопрос-ответ изменен',
      'Ошибка при  изменении вопроса-ответа',
      navigateAfterSuccess,
    );
  };

  const onCategoryChange = (data: number) => {
    setCategoryId(data);
  };

  const onEditorValueChange = (val: string) => {
    setContent(val);
  };

  const publicate = async () => {
    const newFaq = prepareFaqToSave(FaqStatuses.PUBLICATED);
    await addNotifications(
      updateFaqQuery(newFaq),
      'Вопрос-ответ опубликован',
      'Ошибка при  публикации вопроса-ответа',
      navigateAfterSuccess,
    );
  };

  const deleteFaq = async () => {
    await addNotifications(
      deleteFaqQuery({ id: Number(id) }),
      'Вопрос-ответ удален',
      'Ошибка при  удалении вопроса-ответа',
      navigateAfterSuccess,
    );
  };

  const disabled = useMemo(() => {
    return !categoryId || !title || !content || content === '<p><br></p>';
  }, [categoryId, content, title]);

  return (
    <div className={style.wrap}>
      <div className={style['top-block']}>
        <h3>
          <Breadcrumbs breadCrumbs={breadCrumbs} />
        </h3>
        <div className={style.buttons}>
          <Button danger onClick={deleteFaq} loading={isDeleteLoading}>
            Удалить
          </Button>
          <Button type="primary" onClick={publicate} loading={isUpdateLoading}>
            Опубликовать
          </Button>
          <Button onClick={cancelModalOpen}>Отмена</Button>
        </div>

        <ModalCancelSubmit open={openCancelModal} onQuit={onSubmitCancelModal} onStay={onCancelCancelModal} />
      </div>
      <Spin spinning={!data}>
        <Title onTitleChange={onTitleChange} title={title} />
        <div className={style.categories}>
          <h3>Категория</h3>
          <Select
            style={{ width: '100%' }}
            allowClear
            value={categoryId as number | null | undefined}
            onChange={onCategoryChange}
            options={options}
          />
        </div>
        <TextEditor onValueChange={onEditorValueChange} title="Текст ответа на вопрос" content={content} />
        <div className={style['create-btn']}>
          <Button type="primary" onClick={createFaq} loading={isUpdateLoading} disabled={disabled}>
            Сохранить
          </Button>
        </div>
      </Spin>
    </div>
  );
};

export default UpdateFaq;
